import { AsyncPipe } from '@angular/common';
import { Component, inject, input, output, viewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable } from 'rxjs';

import {
    PrivaFilterSearchComponent,
    PrivaFilterSearchConfig,
    PrivaQueryableDataSource,
    PrivaQueryableDataSourceFactory,
} from '@priva/components/filter';
import { PrivaLocalizationModule } from '@priva/localization';

import { TerminalOverlayComponent } from 'app/shared/components';
import { selectEmployeeList } from 'app/state/employee/employee.selectors';
import { selectMultiUserRecentEmployeeNumbersIfLoaded } from 'app/state/multi-user-preferences/multi-user-preferences.selectors';

import { ActivityType } from '../activity-selector/models';
import { EmployeeGroupComponent } from './components/employee-group/employee-group.component';
import { EmployeeNavigationComponent } from './components/employee-navigation/employee-navigation.component';
import { filterResultGroupName, promotedGroupName } from './constants';
import { Employee, EmployeeGroup } from './models';

@Component({
    selector: 'app-employee-selector',
    templateUrl: './employee-selector.component.html',
    styleUrl: './employee-selector.component.scss',
    imports: [
        AsyncPipe,
        PrivaFilterSearchComponent,
        PrivaLocalizationModule,
        EmployeeNavigationComponent,
        EmployeeGroupComponent,
        TerminalOverlayComponent,
    ],
})
export class EmployeeSelectorComponent {
    private store = inject(Store);
    private dataSourceFactory = inject(PrivaQueryableDataSourceFactory);

    title = input<string>('');
    activityType = input<ActivityType | undefined>(ActivityType.Generic);

    employeeSelected = output<Employee>();
    closeRequested = output<void>();

    isNavigating = false;
    employeeGroups$: Observable<EmployeeGroup[]>;

    readonly employeeGroupChildren = viewChildren(EmployeeGroupComponent);

    // Priva search filter
    readonly employeeDataSource: PrivaQueryableDataSource<Employee>;
    filterSearchConfig: PrivaFilterSearchConfig = {
        type: 'search',
        id: 'searchEmployee',
        title: 'GENERAL.TITLE.SEARCH_EMPLOYEE',
        placeholder: 'GENERAL.TITLE.SEARCH_EMPLOYEE',
        options: ['name', 'number'],
        searchOptions: {
            matchType: 'single',
        },
    };

    constructor() {
        this.employeeDataSource = this.dataSourceFactory.create();
        this.employeeDataSource.setData(this.store.select(selectEmployeeList));

        this.employeeGroups$ = combineLatest([
            this.store.pipe(selectMultiUserRecentEmployeeNumbersIfLoaded),
            this.employeeDataSource.results$,
            this.employeeDataSource.queryParams$,
        ]).pipe(
            takeUntilDestroyed(),
            map(([recentEmployeeNumbers, employees, query]) => {
                return EmployeeSelectorComponent.mapToEmployeeGroups(
                    employees,
                    query.search?.searchTerms,
                    recentEmployeeNumbers,
                );
            }),
        );
    }

    onNavigateToEmployeeGroup(groupName: string) {
        this.hideNavigation();
        this.scrollTo(groupName);
    }

    scrollTo(groupName: string) {
        const child = this.employeeGroupChildren()?.find(
            (child) => child.employeeGroup()?.groupName === groupName,
        );
        const nativeElement = child?.elementRef?.nativeElement;
        if (nativeElement) {
            // do async call to let navigation component be hidden before scrolling the selected group into the view
            setTimeout(() => nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }), 10);
        }
    }

    showNavigation(groupName: string) {
        // If group name is Result, disable show navigation to groups
        if (groupName !== filterResultGroupName) this.isNavigating = true;
    }

    hideNavigation() {
        this.isNavigating = false;
    }

    onCloseRequested(): void {
        this.closeRequested.emit();
    }

    onEmployeeSelected(employee: Employee): void {
        this.employeeSelected.emit(employee);
    }

    private static mapToEmployeeGroups(
        employees: Employee[],
        searchTerms: string[] | undefined,
        promotedEmployeeList: number[],
    ) {
        const groupsFlat: EmployeeGroup[] = employees.flatMap((employee) => {
            if (searchTerms && searchTerms?.length > 0) {
                return [{ groupName: filterResultGroupName, employees: [employee] }];
            }

            const firstLetterGroup = {
                groupName: employee.name.charAt(0).toUpperCase(),
                employees: [employee],
            };

            if (promotedEmployeeList.includes(employee.number)) {
                const promotedGroup = { groupName: promotedGroupName, employees: [employee] };

                return [promotedGroup, firstLetterGroup];
            }
            return [firstLetterGroup];
        });

        const groupsUnified = groupsFlat.reduce(
            (prev: { [key: string]: EmployeeGroup }, current): { [key: string]: EmployeeGroup } => {
                const groupName = current.groupName;
                return {
                    ...prev,
                    [groupName]: {
                        groupName: current.groupName,
                        employees: [...(prev[current.groupName]?.employees ?? []), ...current.employees],
                    },
                };
            },
            {},
        );

        const groupsUnsorted = Object.values(groupsUnified);

        return [...groupsUnsorted].sort(EmployeeSelectorComponent.compareGroups);
    }

    private static compareGroups(a: EmployeeGroup, b: EmployeeGroup): number {
        if (a.groupName === promotedGroupName) {
            return -1;
        }
        if (b.groupName === promotedGroupName) {
            return 1;
        }

        return a.groupName > b.groupName ? 1 : -1;
    }
}
